import { Canvas } from "fabric";
import { setCanvas, getCanvas, loadProduct, drawGarment, applyGarmentColor } from "./CanvasManager";
import { objectManager } from "./ObjectManager";
import { propertyManager } from "./PropertyManager";
import { historyService } from "./HistoryService";
import { ProductType } from "../types";
import { useDesignerStore } from "../store";

class DesignerEngine {

  initialize(canvas: Canvas) {
    setCanvas(canvas);

    historyService.save();
  }

  getCanvas() {
    return getCanvas();
  }

  // Bounding size (in cm) of all user-placed artwork on the current side.
  // Canvas units are 10px = 1cm (see ObjectManager.updateSelectedSize).
  getPrintSizeCm(): { width: number; height: number } {
    const canvas = getCanvas();
    if (!canvas) return { width: 0, height: 0 };
    const objs = canvas.getObjects().filter((o: any) => o.selectable !== false && !o.excludeFromExport);
    if (!objs.length) return { width: 0, height: 0 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const o of objs) {
      const r = (o as any).getBoundingRect();
      minX = Math.min(minX, r.left);
      minY = Math.min(minY, r.top);
      maxX = Math.max(maxX, r.left + r.width);
      maxY = Math.max(maxY, r.top + r.height);
    }
    return {
      width: Number(((maxX - minX) / 10).toFixed(1)),
      height: Number(((maxY - minY) / 10).toFixed(1)),
    };
  }

  async loadProduct(product: ProductType) {
    await loadProduct(product);

    historyService.save();
  }

  // Change the garment colour (tints the shirt preview only; artwork untouched).
  async setGarmentColor(hex: string) {
    useDesignerStore.getState().setGarmentColor(hex);
    await applyGarmentColor(hex);
    historyService.save();
  }

  // Switch between the front and back of a t-shirt, preserving each side's work.
  async switchSide(product: ProductType, newSide: "front" | "back") {
    const canvas = getCanvas();
    if (!canvas) return;
    const store = useDesignerStore.getState();
    const current = store.side;
    if (current === newSide) return;

    // Stash the current side's artwork (garment is excluded from export), then
    // load the target side's artwork and redraw the garment behind it.
    store.setSideData(current, JSON.stringify(canvas.toJSON()));
    store.setSide(newSide);
    const target = useDesignerStore.getState().sideData[newSide];
    if (target) {
      await canvas.loadFromJSON(JSON.parse(target));
      await drawGarment(newSide, store.garmentColor);
      canvas.requestRenderAll();
    } else {
      await loadProduct(product);
    }
    historyService.save();
  }

  // Reopen a previously saved design. T-shirts are stored as { sides: {front,back} };
  // gang sheets are a single Fabric JSON.
  async loadSavedDesign(data: string, kind: string) {
    const canvas = getCanvas();
    if (!canvas) return;
    const store = useDesignerStore.getState();
    try {
      const parsed = JSON.parse(data);
      if (kind === "tshirt" && parsed && parsed.sides) {
        if (parsed.color) store.setGarmentColor(parsed.color);
        store.setSideData("front", parsed.sides.front ? JSON.stringify(parsed.sides.front) : null);
        store.setSideData("back", parsed.sides.back ? JSON.stringify(parsed.sides.back) : null);
        store.setSide("front");
        if (parsed.sides.front) await canvas.loadFromJSON(parsed.sides.front);
        else canvas.clear();
        await drawGarment("front", useDesignerStore.getState().garmentColor);
        canvas.requestRenderAll();
      } else {
        await canvas.loadFromJSON(parsed);
        canvas.requestRenderAll();
      }
      historyService.save();
    } catch {
      /* malformed design data — leave the canvas as-is */
    }
  }

  // ============================
  // History
  // ============================

  undo() {
    historyService.undo();
  }

  redo() {
    historyService.redo();
  }

  saveHistory() {
    historyService.save();
  }

  // ============================
  // Objects
  // ============================

  async addLogo(imageUrl: string) {
    await objectManager.addLogo(imageUrl);
  }

  addText(text: string) {
    objectManager.addText(text);
  }

  deleteSelected() {
    objectManager.deleteSelected();
  }

  duplicateSelected() {
    objectManager.duplicateSelected();
  }

  // ======================================
  // Layer Ordering
  // (Temporarily disabled)
  // ======================================

  /*
  bringForward() {
    objectManager.bringForward();
  }

  sendBackward() {
    objectManager.sendBackward();
  }
  */

  // ============================
  // Text Properties
  // ============================

  setFontSize(size: number) {
    propertyManager.setFontSize(size);
  }

  setFontFamily(font: string) {
    propertyManager.setFontFamily(font);
  }

  setFillColor(color: string) {
    propertyManager.setFillColor(color);
  }

  toggleBold() {
    propertyManager.toggleBold();
  }

  toggleItalic() {
    propertyManager.toggleItalic();
  }

}

export const designerEngine = new DesignerEngine();